import {
  MainFeedbackRequest,
  MainUrlWithScore,
  MainUrlWithScoreFromJSON,
} from '../../alma-sdk';
import { type AlmaApi, MainConversationSummary } from '../../types';
import { type UrlWithScore, type AlmaMessage } from './almaTypes';

export function getUniqueReferences(
  refs: Array<UrlWithScore>,
): Array<UrlWithScore> {
  const seen = new Set<string>();
  return refs.filter(r => {
    let u: string | undefined;
    if ('url' in r) u = r.url;
    if ('Url' in r) u = r.Url;
    if (typeof u !== 'string' || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
}

export function extractArticleContent(): string {
  const selectors = [
    'article.md-content__inner.md-typeset',
    'article.md-content__inner',
    'article.md-typeset',
    'article[class*="md-content"]',
    'article',
    '[data-testid="techdocs-content"]',
    '.md-content',
  ] as const;
  for (const s of selectors) {
    const el = document.querySelector(s);
    if (el) return el.innerHTML.trim();
  }
  return '';
}

export async function askAlma(
  almaApi: AlmaApi,
  question: string,
  conversationId: number,
  setConversationId: (id: number) => void,
  pageContext?: string,
) {
  const result = await almaApi.ask(question, conversationId, pageContext);
  if (result.ok) {
    if (
      result.value?.conversationId &&
      result.value.conversationId !== conversationId
    ) {
      setConversationId(result.value.conversationId);
    }
    return result.value;
  }
  throw new Error(`Failed to get answer: ${result.message}`);
}

export async function feedbackAlma(
  almaApi: AlmaApi,
  data: MainFeedbackRequest,
) {
  const result = await almaApi.feedback(data);
  if (result.ok) return result.value;
  throw new Error(`Failed to send feedback: ${result.message}`);
}

export async function getRecentConversations(almaApi: AlmaApi, limit = 20) {
  const result = await almaApi.getRecentConversations(limit);
  return result.ok ? result.value : undefined;
}

export async function getConversationById(
  almaApi: AlmaApi,
  conversationId: number,
) {
  const result = await almaApi.getConversationById(conversationId);
  return result.ok ? result.value : undefined;
}

export function normalizeReferenceItem(r: unknown): MainUrlWithScore | null {
  if (!r || typeof r !== 'object') return null;
  const o = r as Record<string, unknown>;
  const url =
    typeof o.url === 'string'
      ? o.url
      : typeof o.Url === 'string'
        ? o.Url
        : undefined;
  if (!url) return null;
  const scoreRaw = o.score ?? o.Score;
  const score =
    typeof scoreRaw === 'number' && Number.isFinite(scoreRaw)
      ? scoreRaw
      : typeof scoreRaw === 'string' && scoreRaw.trim() !== ''
        ? Number(scoreRaw)
        : undefined;
  return MainUrlWithScoreFromJSON({
    url,
    score:
      typeof score === 'number' && Number.isFinite(score) ? score : undefined,
  });
}

export function normalizeReferencesArray(raw: unknown): MainUrlWithScore[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeReferenceItem)
    .filter((ref): ref is MainUrlWithScore => ref !== null);
}

function decodeEscapedNewlines(value: string): string {
  return value
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t');
}

function cleanupMalformedStoredAssistantText(value: string): string {
  let next = value.trim();
  const answerMatch = next.match(/"answer"\s*:\s*"([\s\S]*)$/);
  if (answerMatch?.[1]) next = answerMatch[1];
  next = next.replace(/","references"\s*:\s*\[[\s\S]*\}\s*$/m, '');
  next = next.replace(/\\"/g, '"');
  return decodeEscapedNewlines(next).trim();
}

function extractReferencesFromMalformedText(value: string): MainUrlWithScore[] {
  const match = value.match(/"references"\s*:\s*(\[[\s\S]*\])\s*\}?\s*$/m);
  if (!match?.[1]) return [];
  try {
    const parsed = JSON.parse(match[1]);
    return Array.isArray(parsed) ? normalizeReferencesArray(parsed) : [];
  } catch {
    return [];
  }
}

export function normalizeRestoredMessage(
  msg: { role: string; content: string },
  idx: number,
): AlmaMessage | null {
  if (typeof msg.content !== 'string') return null;
  if (msg.content.includes('<<Context-Insertion>>')) return null;
  const isUser = msg.role === 'user' || msg.role === 'human';
  if (isUser)
    return {
      id: Date.now() + idx,
      text: msg.content,
      isUser: true,
      feedback: null,
      references: [],
    };
  let text = msg.content;
  let references: MainUrlWithScore[] = [];
  try {
    const parsed = JSON.parse(msg.content) as {
      answer?: unknown;
      references?: unknown;
    };
    if (typeof parsed.answer === 'string' && parsed.answer.trim().length > 0)
      text = parsed.answer;
    if (parsed.references !== undefined)
      references = normalizeReferencesArray(parsed.references);
  } catch {
    /* plain text */
  }
  if (references.length === 0)
    references = extractReferencesFromMalformedText(msg.content);
  text = cleanupMalformedStoredAssistantText(text);
  return {
    id: Date.now() + idx,
    text,
    isUser: false,
    feedback: null,
    references,
  };
}

export function formatConversationLabel(c: MainConversationSummary): string {
  const t = c.title?.trim();
  return t && t.length > 0 ? t : `Conversation #${c.conversationId}`;
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatTimeOfDay(d: Date): string {
  const withMinutes = d.getMinutes() !== 0 || d.getSeconds() !== 0;
  return d
    .toLocaleTimeString(undefined, {
      hour: 'numeric',
      ...(withMinutes ? { minute: '2-digit' as const } : {}),
      hour12: true,
    })
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function formatConversationUpdatedAt(updatedAt: string): string {
  if (!updatedAt?.trim()) return '';
  const d = new Date(updatedAt);
  if (Number.isNaN(d.getTime())) return updatedAt;
  const now = new Date();
  const todayStart = startOfLocalDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const timeStr = formatTimeOfDay(d);
  if (d >= todayStart) return `today ${timeStr}`;
  if (d >= yesterdayStart) return `yesterday ${timeStr}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const datePart = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' as const }),
  });
  return `${datePart} · ${timeStr}`;
}
